import axios, { AxiosResponse } from "axios";
import { HISTORY_API_DOMAIN, P_CHAIN_INDEXER_DOMAIN, TRANSACTION_RETURN_LIMIT } from "../constants";
import {
  BalanceResponse,
  BlocksResponse,
  DelegationsListResponse,
  ExportImportTransactionsResponse,
  FundsHistoryResponse,
  NetworkDataResponse,
  TransactionIdResponse,
  TransactionResponse,
  TransactionsResponse,
  UptimeEpochResponse,
  UtxoResponse,
  ValidatorListResponse,
  ValidatorResponse,
} from "../models/Response";
import { AddressPair } from "../models/AddressPair";
import { Block } from "../models/Block";
import { Balances } from "../models/Balances";
import { Utxo } from "../models/Utxo";
import { NetworkData } from "../models/NetworkData";
import { TransactionType } from "../models/enums";
import { DelegationsList, Validator, ValidatorsInfo } from "../models/Validator";
import { Node, NodesList } from "../models/Node";
import { NodeHistory, UptimeEpochData } from "../models/NodeHistory";
import { ExportImportTransaction, ExportImportType } from "../models/Transaction";

export enum SortDirection {
  asc = "asc",
  desc = "desc",
}

export class IndexerService {

  /**
   * Return transaction data by transaction hash
   * @param txId
   */
  static async getTransaction(txId: string): Promise<TransactionResponse> {
    const tx = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}transactions/get/${txId}`,
    );
    return tx.data;
  }

  static async getTransactionByInputId(inputId: string): Promise<TransactionIdResponse> {
    const response: AxiosResponse<TransactionIdResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}transactions/list/by_input_tx/${inputId}`,
    );
    return response.data;
  }

  static async getTransactionsPerAddress(
    address: string,
    dateFrom: string | null = null,
    dateTo: string | null = null,
    offset = 0,
    limit = TRANSACTION_RETURN_LIMIT,
    direction = "desc",
    type?: TransactionType | null | undefined,
  ): Promise<TransactionsResponse> {
    const txs = await axios.get(`${P_CHAIN_INDEXER_DOMAIN}transactions/list`, {
      params: {
        inputAddress: address,
        outputAddress: address,
        direction: direction,
        startChainTime: dateFrom,
        endChainTime: dateTo,
        offset: offset,
        limit: limit,
        types: type ?? undefined,
      },
    });
    return txs.data;
  }

  static async getBlocks(
    direction?: SortDirection,
    limit?: number,
    offset?: number,
    includeEmptyBlocks?: boolean,
    types?: string,

  ): Promise<BlocksResponse> {
    const response = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}transactions/blocks`,
      {
        params: {
          direction: direction,
          limit: limit,
          offset: offset,
          includeEmptyBlocks: includeEmptyBlocks,
          types: types,
        },
      },
    );
    return response.data;
  }

  static async getAddressPair(address: string): Promise<AddressPair | null> {

    try {
      const response: AxiosResponse = await axios.get(
        `${P_CHAIN_INDEXER_DOMAIN}api/v0/address/${address}`,
      );
      return response.data;
    } catch (e) {
      return null;
    }
  }

  static async getLastBlock(): Promise<number> {
    const response = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}transactions/max_block_height`,
    );
    return response.data.data;
  }

  static async getBlocksPerPage(
    page: number,
    limit: number,
    includeEmptyBlocks: boolean,
    types?: string,
  ): Promise<Block[]> {
    const offset = (page - 1) * limit;
    const response = await this.getBlocks(
      SortDirection.desc,
      limit,
      offset,
      includeEmptyBlocks,
      types
    );
    return response.data;
  }

  static async getLastBlocks(
    numberOfBlocks: number,
    includeEmptyBlocks: boolean,
    types?: string,
  ): Promise<Block[] | null> {
    const response: BlocksResponse = await this.getBlocks(
      SortDirection.desc,
      numberOfBlocks,
      undefined,
      includeEmptyBlocks,
      types,
    );

    return response.data;
  }

  static async getAddressBalances(address: string): Promise<Balances> {
    const response: AxiosResponse<BalanceResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}state/balance/${address}`,
    );
    return response.data.data;
  }

  static async getAddressFundsHistory(
    address: string,
    limit?: number,
    offset?: number,
    statuses?: string[],
  ): Promise<FundsHistoryResponse> {
    const response: AxiosResponse<FundsHistoryResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}transactions/funds_history`,
      {
        params: {
          address: address,
          limit: limit,
          offset: offset,
          statuses: statuses,
        },
        paramsSerializer: {
          indexes: null, // by default: false
        },
      },
    );
    return response.data;
  }

  static async getCUtxos(address: string): Promise<Utxo[]> {
    const response: AxiosResponse<UtxoResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}utxos/c_utxo/${address}`,
    );
    if (response.status === 500) {
      throw new Error("Data could not be retrieved");
    }
    return response.data.data ?? [];
  }

  static async getPUtxos(address: string): Promise<Utxo[]> {
    const response: AxiosResponse<UtxoResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}utxos/p_utxo/${address}`,
    );
    if (response.status === 500) {
      throw new Error("Data could not be retrieved");
    }
    return response.data.data ?? [];
  }

  static async getNetworkData(): Promise<NetworkData> {
    const response: AxiosResponse<NetworkDataResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}state/networkOverview`,
    );
    return response.data.data;
  }

  static async getValidatorList(): Promise<ValidatorsInfo> {
    const response: AxiosResponse<ValidatorListResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}validators/listValidators`,
    );
    return response.data.data;
  }

  static async getValidator(nodeId: string): Promise<Validator> {
    const response: AxiosResponse<ValidatorResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}validators/getValidatorData/${nodeId}`,
    );
    return response.data.data;
  }

  static async getValidatorDelegations(
    nodeId: string,
    limit?: number,
    offset?: number,
    amountSortDirection: "desc" | "asc" | undefined = undefined,
    endTimeSortDirection: "desc" | "asc" = "asc",
  ): Promise<DelegationsList> {
    const response: AxiosResponse<DelegationsListResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}validators/listValidatorDelegations`,
      {
        params: {
          nodeID: nodeId,
          limit: limit,
          offset: offset,
          amountSortDirection: amountSortDirection,
          endTimeSortDirection: endTimeSortDirection,
        },
      },
    );
    return response.data.data;
  }

  static async getUptimeData(nodeId: string): Promise<UptimeEpochData | null> {
    const response: AxiosResponse<UptimeEpochResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}uptime/epoch`,
      {
        params: {
          nodeId: nodeId,
        },
      },
    );
    return response.data.data[0] ?? null;
  }

  /** EVM INDEXER API **/
  static async getCChainTransactions(
    address: string,
    timestamp_from: number | null = null,
    timestamp_to: number | null = null,
    offset = 0,
    limit = TRANSACTION_RETURN_LIMIT,
    sort = "desc",
    type?: ExportImportType | null | undefined,
  ): Promise<ExportImportTransactionsResponse> {
    const response: AxiosResponse<ExportImportTransactionsResponse> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}api/v0/export_import_transactions`,
      {
        params: {
          address: address,
          limit: limit,
          offset: offset,
          sort: sort,
          timestamp_from: timestamp_from,
          timestamp_to: timestamp_to,
          type: type ?? null,
        },
      },
    );
    return response.data;
  }

  static async getCChainTransaction(
    id: string,
  ): Promise<ExportImportTransaction> {
    const response: AxiosResponse<ExportImportTransaction> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}api/v0/export_import_transactions/${id}`,
    );
    return response.data;
  }

  static async getCChainTransactionByImportedInputId(
    id: string,
  ): Promise<ExportImportTransaction> {
    const response: AxiosResponse<ExportImportTransaction> = await axios.get(
      `${P_CHAIN_INDEXER_DOMAIN}api/v0/export_import_transactions/by_imported_input_tx_id`,
      {
        params: {
          tx_id: id,
        },
      },
    );
    return response.data;
  }


  /** HISTORY API ENDPOINTS **/
  static async getNodes(): Promise<NodesList> {
    const response: AxiosResponse<NodesList> = await axios.get(
      `${HISTORY_API_DOMAIN}history/pchain/nodes`,
    );
    return response.data;
  }

  static async getNode(nodeId: string): Promise<Node | null> {
    const response: AxiosResponse<NodesList> = await axios.get(
      `${HISTORY_API_DOMAIN}history/pchain/nodes`,
      {
        params: {
          nodeId: nodeId,
        },
      },
    );
    return response.data.nodes[0] ?? null;
  }

  static async getNodeHistory(nodeId: string): Promise<NodeHistory> {
    const response: AxiosResponse<NodeHistory> = await axios.get(
      `${HISTORY_API_DOMAIN}history/pchain/nodeHistory`,
      {
        params: {
          nodeId: nodeId,
        },
      },
    );
    return response.data;
  }
}
